import { type AdminTagResponse, toAdminTag } from 'src/models/apis/tag/adminTagResponse';
import type {
  AdminMovieTag,
  AdminMovieCore,
  AdminMovieGroup,
  AdminMovieGroupUpdateMovieTagsResponse,
  AdminMovieFile,
  AdminDestroyMovie,
  AdminMovieGroupDestroyResponse,
} from 'src/models/movieGroup';

export interface AdminPivot {
  movie_id: number;
  tag_id: number;
  updated_at: string;
  created_at: string;
}

export interface AdminMovieTagResponse extends AdminTagResponse {
  pivot: AdminPivot;
}

export interface AdminMovieCoreResponse {
  device_id: string;
  duration: string;
  id: number;
  lat: string;
  lon: string;
  movie_group_id: number;
  start_ts: string;
  tags: AdminMovieTagResponse[];
}

export interface AdminMovieGroupResponse {
  id: number;
  device_id: string;
  start_ts: string;
  end_ts: string;
  car_name: string;
  movies: AdminMovieCoreResponse[];
}

export type AdminMovieGroupIndexResponse = AdminMovieGroupResponse[];

export interface AdminMovieGroupUpdateMovieTagsResponseRaw {
  id: number;
  device_id: string;
  start_ts: string;
  end_ts: string;
  movies: AdminMovieCoreResponse[];
}

export interface AdminMovieFileResponse {
  id: number;
  movie_id: number;
  movie_path: string;
  camera_position: string;
}

export interface AdminDestroyMovieResponse {
  id: number;
  device_id: string;
  duration: string;
  lat: string;
  lon: string;
  movie_group_id: number;
  start_ts: string;
  movie_files: AdminMovieFileResponse[];
}

export interface AdminMovieGroupDestroyResponseRaw {
  id: number;
  device_id: string;
  start_ts: string;
  end_ts: string;
  movies: AdminDestroyMovieResponse[];
}

export type AdminMovieGroupCollectionDestroyResponse = AdminMovieGroupDestroyResponseRaw[];

export const toAdminMovieTag = (response: AdminMovieTagResponse): AdminMovieTag => toAdminTag(response);

export const toAdminMovie = (response: AdminMovieCoreResponse): AdminMovieCore => ({
  deviceId: response.device_id,
  duration: response.duration,
  id: response.id,
  location: { lat: response.lat, lon: response.lon },
  movieGroupId: response.movie_group_id,
  startTs: response.start_ts,
  tags: response.tags.map(toAdminMovieTag),
});

export const toAdminMovieGroup = (response: AdminMovieGroupResponse): AdminMovieGroup => ({
  id: response.id,
  deviceId: response.device_id,
  startTs: response.start_ts,
  endTs: response.end_ts,
  carName: response.car_name,
  movies: response.movies.map(toAdminMovie),
});

export const toAdminMovieGroupUpdateMovieTags = (
  response: AdminMovieGroupUpdateMovieTagsResponseRaw,
): AdminMovieGroupUpdateMovieTagsResponse => ({
  id: response.id,
  deviceId: response.device_id,
  startTs: response.start_ts,
  endTs: response.end_ts,
  movies: response.movies.map(toAdminMovie),
});

export const toAdminMovieFile = (response: AdminMovieFileResponse): AdminMovieFile => ({
  id: response.id,
  movieId: response.movie_id,
  moviePath: response.movie_path,
  cameraPosition: response.camera_position,
});

export const toAdminDestroyMovie = (response: AdminDestroyMovieResponse): AdminDestroyMovie => ({
  id: response.id,
  deviceId: response.device_id,
  duration: response.duration,
  location: { lat: response.lat, lon: response.lon },
  movieGroupId: response.movie_group_id,
  startTs: response.start_ts,
  movieFiles: response.movie_files.map(toAdminMovieFile),
});

export const toAdminMovieGroupDestroy = (
  response: AdminMovieGroupDestroyResponseRaw,
): AdminMovieGroupDestroyResponse => ({
  id: response.id,
  deviceId: response.device_id,
  startTs: response.start_ts,
  endTs: response.end_ts,
  movies: response.movies.map(toAdminDestroyMovie),
});
